import React, { useState, useEffect, useRef } from 'react';
import {
    TextField, Button, Box, Typography, Select, MenuItem, FormControl, InputLabel,
    ToggleButtonGroup, ToggleButton, InputAdornment, List, ListItem, ListItemText, IconButton, Divider, Alert, RadioGroup, FormControlLabel, Radio, FormLabel,
    Autocomplete, CircularProgress, FormHelperText,
    Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    ListSubheader,
    Stepper, Step, StepButton,
    Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, Chip
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';

import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { getObras, getFornecedores, getInvestidores, getCategoriasFlat, getFasesDeObra, getItens, updateMultipleItemPrices, Obra, Fornecedor, Investidor, Categoria, FaseDeObra as FaseObra, ItemCatalogo as Item } from '../services/api';
import { getHierarchicalCategories } from '../utils/categoryUtils';

// Zod Schema
const itemSchema = z.object({
    fase_obra_id: z.string().min(1, "Obrigatório"),
    categoria_id: z.string().min(1, "Obrigatório"),
    descricao: z.string().min(1, "Obrigatório"),
    catalogo_item_id: z.number().nullable().optional(),
    quantidade: z.coerce.number().min(0.01, "Obrigatório"),
    valor_unitario: z.coerce.number().min(0, "Inválido"),
    desconto_valor: z.coerce.number().min(0).optional().default(0),
});

const formSchema = z.object({
    obra_id: z.string().min(1, "Obra é obrigatória"),
    fornecedor_id: z.string().min(1, "Fornecedor é obrigatório"),
    investidor_id: z.string().min(1, "Investidor é obrigatório"),
    numero_nota: z.string().optional(),
    data_lancamento: z.string().min(1, "Data é obrigatória").refine((date) => {
        const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().split('T')[0];
        return date <= today;
    }, "Data não pode ser no futuro"),
    status_pagamento: z.string().min(1, "Status é obrigatório"),
    tipo_desconto: z.enum(['total', 'item']),
    desconto_valor: z.coerce.number().optional().default(0),
    desconto_percentual: z.coerce.number().optional().default(0),
    observacoes: z.string().optional(),
    items: z.array(itemSchema).min(1, "Adicione pelo menos 1 item"),
});

type FormValues = z.infer<typeof formSchema>;

export interface LancamentoItemData {
    descricao: string;
    quantidade: number | string;
    valor_unitario: number | string;
    categoria_id: number | string;
    catalogo_item_id?: number | null;
    desconto_valor: number | string;
    fase_obra_id: string;
}

export interface LancamentoFormData {
    id?: number;
    obra_id: string;
    fornecedor_id: string;
    investidor_id: string;
    data_lancamento: string;
    status_pagamento: string;
    numero_nota: string;
    observacoes: string;
    tipo_desconto: 'total' | 'item';
    desconto_valor: number | string;
    desconto_percentual: number | string;
    items: LancamentoItemData[];
    anexos?: any[];
}

export interface LancamentoBaseFormProps {
    initialData?: Partial<LancamentoFormData>;
    initialStep?: number;
    isEditMode: boolean;
    onSubmit: (data: LancamentoFormData, novosAnexos: File[], anexosDeletadosIds: number[]) => Promise<void>;
    onCancel: () => void;
    isLoadingSubmit?: boolean;
}

const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
};

export const LancamentoBaseForm: React.FC<LancamentoBaseFormProps> = ({ initialData, initialStep = 0, isEditMode, onSubmit, onCancel, isLoadingSubmit }) => {
    const [obras, setObras] = useState<Obra[]>([]);
    const [fornecedores, setFornecedores] = useState<Fornecedor[]>([]);
    const [investidores, setInvestidores] = useState<Investidor[]>([]);
    const [categorias, setCategorias] = useState<Categoria[]>([]);
    const [fasesDeObra, setFasesDeObra] = useState<FaseObra[]>([]);
    const [catalogoItens, setCatalogoItens] = useState<Item[]>([]);

    const [descontoTipoUI, setDescontoTipoUI] = useState<'valor' | 'percentual'>('valor');
    const [anexos, setAnexos] = useState<File[]>([]);
    const [anexosDeletadosIds, setAnexosDeletadosIds] = useState<number[]>([]);
    
    const [error, setError] = useState<string>('');
    const [loadingData, setLoadingData] = useState(true);
    const [isReviewing, setIsReviewing] = useState(false);
    const [activeStep, setActiveStep] = useState(initialStep);

    // Estado para atualização de valor padrão no catálogo
    const [priceUpdateModalOpen, setPriceUpdateModalOpen] = useState(false);
    const [divergentItems, setDivergentItems] = useState<{
        catalogo_item_id: number;
        descricao: string;
        valor_catalogo: number;
        novo_valor: number;
    }[]>([]);
    const [selectedCatalogIds, setSelectedCatalogIds] = useState<number[]>([]);
    const [isUpdatingPrices, setIsUpdatingPrices] = useState(false);
    
    const STEPS = ['Dados Gerais', 'Itens e Descontos', 'Anexos', 'Observações e Revisão'];
    
    const fileInputRef = useRef<HTMLInputElement>(null);

    const { control, handleSubmit, watch, setValue, getValues, trigger, formState: { errors } } = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        mode: 'onTouched',
        defaultValues: {
            obra_id: initialData?.obra_id || '',
            fornecedor_id: initialData?.fornecedor_id || '',
            investidor_id: initialData?.investidor_id || '',
            numero_nota: initialData?.numero_nota || '',
            data_lancamento: initialData?.data_lancamento || '',
            status_pagamento: initialData?.status_pagamento || 'Pago',
            tipo_desconto: initialData?.tipo_desconto || 'total',
            desconto_valor: initialData?.desconto_valor ? Number(initialData.desconto_valor) : 0,
            desconto_percentual: initialData?.desconto_percentual ? Number(initialData.desconto_percentual) : 0,
            observacoes: initialData?.observacoes || '',
            items: initialData?.items && initialData.items.length > 0 ? initialData.items.map(i => ({
                fase_obra_id: String(i.fase_obra_id),
                categoria_id: String(i.categoria_id),
                descricao: i.descricao,
                catalogo_item_id: i.catalogo_item_id || null,
                quantidade: Number(i.quantidade) || 1,
                valor_unitario: Number(i.valor_unitario) || 0,
                desconto_valor: Number(i.desconto_valor) || 0,
            })) : [{ fase_obra_id: '', categoria_id: '', descricao: '', catalogo_item_id: null, quantidade: 1, valor_unitario: 0, desconto_valor: 0 }]
        }
    });

    const { fields: itemsFields, append, remove } = useFieldArray({
        control,
        name: "items"
    });

    // Real-time watched values for calculations
    const watchedItems = watch("items");
    const watchedTipoDesconto = watch("tipo_desconto");
    const watchedDescontoValor = watch("desconto_valor");
    const watchedDescontoPercentual = watch("desconto_percentual");

    // Calculando totais on-the-fly (sem useState)
    const valorTotalBruto = watchedItems.reduce((sum, item) => sum + ((Number(item.quantidade) || 0) * (Number(item.valor_unitario) || 0)), 0);
    
    let valorTotalLiquido = 0;
    if (watchedTipoDesconto === 'item') {
        valorTotalLiquido = watchedItems.reduce((sum, item) => {
            const itemBruto = (Number(item.quantidade) || 0) * (Number(item.valor_unitario) || 0);
            return sum + (itemBruto - (Number(item.desconto_valor) || 0));
        }, 0);
    } else {
        const descValue = descontoTipoUI === 'valor' ? (Number(watchedDescontoValor) || 0) : 0;
        const descPerc = descontoTipoUI === 'percentual' ? (Number(watchedDescontoPercentual) || 0) : 0;
        valorTotalLiquido = descPerc > 0 ? valorTotalBruto * (1 - descPerc / 100) : valorTotalBruto - descValue;
    }

    // Calcula valor unitario liquido e total liquido de cada item (para o modal de revisão)
    const getCalculatedItems = () => {
        return watchedItems.map(item => {
            const itemBruto = (Number(item.quantidade) || 0) * (Number(item.valor_unitario) || 0);
            const proporcao = valorTotalBruto > 0 ? itemBruto / valorTotalBruto : 0;
            const valor_liquido_item = watchedTipoDesconto === 'total' 
                ? proporcao * valorTotalLiquido 
                : itemBruto - (Number(item.desconto_valor) || 0);
            const valor_unitario_liquido = (Number(item.quantidade) || 0) > 0 ? valor_liquido_item / (Number(item.quantidade) || 1) : 0;
            return { ...item, valor_unitario_liquido, total_liquido: valor_liquido_item };
        });
    };

    useEffect(() => {
        if (initialData?.desconto_percentual && Number(initialData.desconto_percentual) > 0) {
            setDescontoTipoUI('percentual');
        } else {
            setDescontoTipoUI('valor');
        }
        
        const fetchData = async () => {
            setLoadingData(true);
            try {
                const [obrasRes, fornecedoresRes, investidoresRes, categoriasRes, fasesDeObraRes, itensRes] = await Promise.all([
                    getObras(), getFornecedores(), getInvestidores(), getCategoriasFlat(), getFasesDeObra(), getItens({})
                ]);
                setObras(obrasRes || []);
                setFornecedores(fornecedoresRes || []);
                setInvestidores(investidoresRes || []);
                setCategorias(categoriasRes || []);
                setFasesDeObra(fasesDeObraRes || []);
                setCatalogoItens(itensRes || []);
            } catch (error) {
                console.error("Erro ao buscar dados", error);
                setError("Não foi possível carregar os dados auxiliares.");
            } finally {
                setLoadingData(false);
            }
        };
        fetchData();
    }, [initialData]);

    const handleDescontoTipoChange = (event: React.MouseEvent<HTMLElement>, newDescontoTipo: 'valor' | 'percentual' | null) => {
        if (newDescontoTipo) {
            setDescontoTipoUI(newDescontoTipo);
            setValue('desconto_valor', 0);
            setValue('desconto_percentual', 0);
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files) {
            const newFiles = Array.from(files);
            setAnexos(prev => {
                const existingFiles = prev.map(f => f.name);
                const uniqueNewFiles = newFiles.filter(f => !existingFiles.includes(f.name));
                return [...prev, ...uniqueNewFiles];
            });
        }
    };

    const handleRemoveAnexo = (indexToRemove: number) => {
        setAnexos(prev => prev.filter((_, index) => index !== indexToRemove));
    };

    const handleRemoveExistingAnexo = (anexoId: number) => {
        setAnexosDeletadosIds(prev => [...prev, anexoId]);
    };

    // Validação por passo do stepper
    const validateStep = async (step: number): Promise<boolean> => {
        const fieldsToValidate: Record<number, (keyof FormValues)[]> = {
            0: ['obra_id', 'fornecedor_id', 'investidor_id', 'data_lancamento', 'status_pagamento'],
            1: ['items', 'tipo_desconto', 'desconto_valor', 'desconto_percentual'],
        };
        const fields = fieldsToValidate[step];
        if (!fields) return true;
        const result = await trigger(fields);
        return result;
    };

    const handleNext = async () => {
        const isValid = await validateStep(activeStep);
        if (isValid && activeStep < STEPS.length - 1) {
            setActiveStep(prev => prev + 1);
        }
    };

    const handleBack = () => {
        if (activeStep > 0) {
            setActiveStep(prev => prev - 1);
        }
    };

    const handleStepClick = (step: number) => {
        setActiveStep(step);
    };

    const onFormSubmit = (data: FormValues) => {
        setIsReviewing(true);
        setError('');
    };

    const executeFinalSubmit = async (updateCatalog: boolean) => {
        const data = getValues();
        setIsUpdatingPrices(true);
        try {
            if (updateCatalog && selectedCatalogIds.length > 0) {
                const updates = divergentItems
                    .filter(item => selectedCatalogIds.includes(item.catalogo_item_id))
                    .map(item => ({
                        id: item.catalogo_item_id,
                        valor_unitario_padrao: item.novo_valor
                    }));
                if (updates.length > 0) {
                    await updateMultipleItemPrices(updates);
                    // Atualiza estado local do catálogo
                    setCatalogoItens(prev =>
                        prev.map(ci => {
                            const upd = updates.find(u => u.id === ci.id);
                            return upd ? { ...ci, valor_unitario_padrao: upd.valor_unitario_padrao } : ci;
                        })
                    );
                }
            }
            setPriceUpdateModalOpen(false);
            await onSubmit(data as unknown as LancamentoFormData, anexos, anexosDeletadosIds);
        } catch (err: any) {
            setError(err.message || 'Erro ao processar lançamento.');
        } finally {
            setIsUpdatingPrices(false);
        }
    };

    const onConfirmSubmit = async () => {
        const data = getValues();
        // Verifica itens vinculados ao catálogo cujo valor unitário difere do cadastrado
        const mapDivergent = new Map<number, {
            catalogo_item_id: number;
            descricao: string;
            valor_catalogo: number;
            novo_valor: number;
        }>();

        (data.items || []).forEach(item => {
            if (!item.catalogo_item_id) return;
            const catItem = catalogoItens.find(ci => ci.id === Number(item.catalogo_item_id));
            if (!catItem) return;

            const novoValor = Number(item.valor_unitario) || 0;
            const valorCatalogo = Number(catItem.valor_unitario_padrao) || 0;

            if (novoValor > 0 && Math.abs(novoValor - valorCatalogo) >= 0.01) {
                mapDivergent.set(catItem.id, {
                    catalogo_item_id: catItem.id,
                    descricao: catItem.descricao,
                    valor_catalogo: valorCatalogo,
                    novo_valor: novoValor
                });
            }
        });

        const divergentList = Array.from(mapDivergent.values());
        if (divergentList.length > 0) {
            setDivergentItems(divergentList);
            // Marca por padrão itens que já possuíam valor > 0 cadastrado no catálogo;
            // Itens com R$ 0,00 (ex.: serviços/mão de obra variável) vêm desmarcados por padrão
            setSelectedCatalogIds(
                divergentList.filter(d => d.valor_catalogo > 0).map(d => d.catalogo_item_id)
            );
            setPriceUpdateModalOpen(true);
            return;
        }

        await executeFinalSubmit(false);
    };

    const renderReviewSummary = () => {
        const calculatedItems = getCalculatedItems();
        const data = getValues();
        return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
                Confira os dados abaixo antes de {isEditMode ? 'salvar as alterações' : 'adicionar o lançamento'}.
            </Alert>

            <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Dados Gerais</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                    <Typography><strong>Obra:</strong> {obras.find(o => String(o.id) === String(data.obra_id))?.nome}</Typography>
                    <Typography><strong>Data:</strong> {data.data_lancamento ? new Date(data.data_lancamento).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '-'}</Typography>
                    <Typography><strong>Fornecedor:</strong> {fornecedores.find(f => String(f.id) === String(data.fornecedor_id))?.nome_razao_social || '-'}</Typography>
                    <Typography><strong>Investidor:</strong> {investidores.find(i => String(i.id) === String(data.investidor_id))?.nome || '-'}</Typography>
                    <Typography><strong>Nota Fiscal:</strong> {data.numero_nota || '-'}</Typography>
                    <Typography><strong>Status:</strong> {data.status_pagamento}</Typography>
                </Box>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Itens ({calculatedItems.length})</Typography>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Fase</TableCell>
                                <TableCell>Categoria</TableCell>
                                <TableCell>Descrição</TableCell>
                                <TableCell align="right">Qtd</TableCell>
                                <TableCell align="right">Vl. Unit.</TableCell>
                                <TableCell align="right">Total Líquido</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {calculatedItems.map((item, index) => (
                                <TableRow key={index}>
                                    <TableCell>{fasesDeObra.find(f => String(f.id) === String(item.fase_obra_id))?.nome || '-'}</TableCell>
                                    <TableCell>{categorias.find(c => String(c.id) === String(item.categoria_id))?.nome || '-'}</TableCell>
                                    <TableCell>{item.descricao}</TableCell>
                                    <TableCell align="right">{item.quantidade}</TableCell>
                                    <TableCell align="right">{formatCurrency(Number(item.valor_unitario))}</TableCell>
                                    <TableCell align="right">{formatCurrency(item.total_liquido)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Totais</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1">Valor Bruto: <strong>{formatCurrency(valorTotalBruto)}</strong></Typography>
                    <Typography variant="body1" color="error">Descontos: <strong>{formatCurrency(valorTotalBruto - valorTotalLiquido)}</strong></Typography>
                    <Typography variant="h5" color="primary">Total Líquido: <strong>{formatCurrency(valorTotalLiquido)}</strong></Typography>
                </Box>
            </Paper>

            {(anexos.length > 0 || (initialData?.anexos && initialData.anexos.length > 0)) && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>Anexos</Typography>
                    <List dense>
                        {initialData?.anexos?.filter((a: any) => !anexosDeletadosIds.includes(a.id)).map((a: any, i: number) => (
                            <ListItem key={`existing-${i}`}><ListItemText primary={a.nome_original} secondary="(Já anexado)" /></ListItem>
                        ))}
                        {anexos.map((a, i) => (
                            <ListItem key={`new-${i}`}><ListItemText primary={a.name} secondary="(Novo)" /></ListItem>
                        ))}
                    </List>
                </Paper>
            )}

            {data.observacoes && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>Observações</Typography>
                    <Typography>{data.observacoes}</Typography>
                </Paper>
            )}

            <Box sx={{ display: 'flex', gap: 2, mt: 2, justifyContent: 'flex-end' }}>
                <Button onClick={() => setIsReviewing(false)} variant="outlined" disabled={isLoadingSubmit}>Voltar e Editar</Button>
                <Button onClick={onConfirmSubmit} variant="contained" color="success" disabled={isLoadingSubmit}>
                    {isLoadingSubmit ? <CircularProgress size={24} /> : (isEditMode ? 'Confirmar e Salvar' : 'Confirmar e Adicionar')}
                </Button>
            </Box>
        </Box>
    )};

    return (
        <Box component="form" onSubmit={handleSubmit(onFormSubmit)} sx={isEditMode ? {} : { padding: 2, border: '1px solid #ccc', borderRadius: '8px', marginBottom: 4 }}>
            {!isEditMode && <Typography variant="h6" gutterBottom>{isReviewing ? 'Revisar Novo Lançamento' : 'Adicionar Novo Lançamento'}</Typography>}
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loadingData && <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}><CircularProgress /></Box>}

            {/* Stepper Navigation */}
            {!isReviewing && !loadingData && (
                <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3, mt: 1 }}>
                    {STEPS.map((label, index) => (
                        <Step key={label} completed={activeStep > index}>
                            <StepButton onClick={() => handleStepClick(index)}>
                                {label}
                            </StepButton>
                        </Step>
                    ))}
                </Stepper>
            )}

            {!isReviewing ? (
                <>
                    {/* STEP 0: Dados Gerais */}
                    {activeStep === 0 && (
                    <>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                        <Controller name="obra_id" control={control} render={({ field }) => (
                            <Autocomplete
                                sx={{ flex: '1 1 23%' }}
                                options={obras}
                                getOptionLabel={(option) => option.nome}
                                value={obras.find(o => String(o.id) === String(field.value)) || null}
                                onChange={(_, newValue) => field.onChange(newValue ? String(newValue.id) : '')}
                                renderInput={(params) => (
                                    <TextField {...params} label="Obra" required error={!!errors.obra_id} helperText={errors.obra_id?.message} />
                                )}
                                isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                                noOptionsText="Nenhuma obra encontrada"
                            />
                        )} />

                        <Controller name="fornecedor_id" control={control} render={({ field }) => (
                            <Autocomplete
                                sx={{ flex: '1 1 23%' }}
                                options={fornecedores.filter((f, i, arr) => arr.findIndex(x => x.id === f.id) === i)}
                                getOptionLabel={(option) => option.nome_razao_social}
                                filterOptions={(options, { inputValue }) => {
                                    const search = inputValue.toLowerCase();
                                    return options.filter(o =>
                                        o.nome_razao_social?.toLowerCase().includes(search) ||
                                        o.cnpj_cpf?.toLowerCase().includes(search) ||
                                        o.tipo?.toLowerCase().includes(search)
                                    );
                                }}
                                value={fornecedores.find(f => String(f.id) === String(field.value)) || null}
                                onChange={(_, newValue) => field.onChange(newValue ? String(newValue.id) : '')}
                                renderOption={(props, option) => (
                                    <li {...props} key={option.id}>
                                        <Box>
                                            <Typography variant="body1">{option.nome_razao_social}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {option.cnpj_cpf || 'Sem CPF/CNPJ'}{option.tipo ? ` · ${option.tipo}` : ''}
                                            </Typography>
                                        </Box>
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField {...params} label="Fornecedor" required error={!!errors.fornecedor_id} helperText={errors.fornecedor_id?.message} />
                                )}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                noOptionsText="Nenhum fornecedor encontrado"
                            />
                        )} />

                        <Controller name="investidor_id" control={control} render={({ field }) => (
                            <Autocomplete
                                sx={{ flex: '1 1 23%' }}
                                options={investidores}
                                getOptionLabel={(option) => option.nome}
                                value={investidores.find(i => String(i.id) === String(field.value)) || null}
                                onChange={(_, newValue) => field.onChange(newValue ? String(newValue.id) : '')}
                                renderInput={(params) => (
                                    <TextField {...params} label="Investidor" required error={!!errors.investidor_id} helperText={errors.investidor_id?.message} />
                                )}
                                isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                                noOptionsText="Nenhum investidor encontrado"
                            />
                        )} />

                        <Controller name="numero_nota" control={control} render={({ field }) => (
                            <TextField {...field} label="Nº da Nota/Recibo" error={!!errors.numero_nota} helperText={errors.numero_nota?.message} sx={{ flex: '1 1 23%' }} />
                        )} />
                        
                        <Controller name="data_lancamento" control={control} render={({ field }) => {
                            const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().split('T')[0];
                            return (
                                <TextField {...field} label="Data da Nota" type="date" InputLabelProps={{ shrink: true }} inputProps={{ max: today }} required error={!!errors.data_lancamento} helperText={errors.data_lancamento?.message} sx={{ flex: '1 1 23%' }} />
                            );
                        }} />

                        <Controller name="status_pagamento" control={control} render={({ field }) => (
                            <FormControl fullWidth error={!!errors.status_pagamento} sx={{ flex: '1 1 23%' }}>
                                <InputLabel>Status Pagamento</InputLabel>
                                <Select {...field} label="Status Pagamento">
                                    <MenuItem value="A Pagar">A Pagar</MenuItem>
                                    <MenuItem value="Pago">Pago</MenuItem>
                                </Select>
                                {errors.status_pagamento && <FormHelperText>{errors.status_pagamento.message}</FormHelperText>}
                            </FormControl>
                        )} />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <Button variant="contained" onClick={handleNext}>Próximo: Itens →</Button>
                    </Box>
                    </>
                    )}

                    {/* STEP 1: Itens do Lançamento */}
                    {activeStep === 1 && (
                    <>
                    {errors.items && !Array.isArray(errors.items) && <Alert severity="error" sx={{ mb: 2 }}>{errors.items.message}</Alert>}
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {itemsFields.map((field, index) => (
                            <Box key={field.id} sx={{ display: 'flex', flexDirection: 'column', gap: 2, border: '1px solid', borderColor: errors.items?.[index] ? 'error.main' : '#eee', p: 2, borderRadius: 1, backgroundColor: '#fafafa' }}>
                                <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Controller name={`items.${index}.fase_obra_id`} control={control} render={({ field: fieldProps }) => (
                                            <FormControl fullWidth required error={!!errors.items?.[index]?.fase_obra_id}>
                                                <InputLabel>Fase</InputLabel>
                                                <Select {...fieldProps} label="Fase">
                                                    <ListSubheader>🏗️ Construção</ListSubheader>
                                                    {fasesDeObra.filter(f => f.tipo_fluxo === 'construcao').map(f => <MenuItem key={f.id} value={String(f.id)} sx={{ pl: 4 }}>{f.nome}</MenuItem>)}
                                                    <ListSubheader>🏠 Leilão</ListSubheader>
                                                    {fasesDeObra.filter(f => f.tipo_fluxo === 'leilao').map(f => <MenuItem key={f.id} value={String(f.id)} sx={{ pl: 4 }}>{f.nome}</MenuItem>)}
                                                </Select>
                                                {errors.items?.[index]?.fase_obra_id && <FormHelperText>{errors.items[index]?.fase_obra_id?.message}</FormHelperText>}
                                            </FormControl>
                                        )} />
                                    </Box>

                                    <Box sx={{ flex: 2 }}>
                                        <Controller name={`items.${index}.descricao`} control={control} render={({ field: fieldProps }) => (
                                            <Autocomplete
                                                freeSolo
                                                fullWidth
                                                options={catalogoItens.filter(ci => !watchedItems[index]?.fase_obra_id || (ci.fases && ci.fases.some((f: any) => String(f.id) === String(watchedItems[index].fase_obra_id))))}
                                                getOptionLabel={(option) => typeof option === 'string' ? option : option.descricao}
                                                value={catalogoItens.find(ci => ci.id === watchedItems[index]?.catalogo_item_id) || fieldProps.value || ''}
                                                onChange={(event, newValue) => {
                                                    if (typeof newValue === 'string') {
                                                        fieldProps.onChange(newValue);
                                                        setValue(`items.${index}.catalogo_item_id`, null);
                                                    } else if (newValue) {
                                                        fieldProps.onChange(newValue.descricao);
                                                        setValue(`items.${index}.catalogo_item_id`, newValue.id);
                                                        setValue(`items.${index}.valor_unitario`, newValue.valor_unitario_padrao);
                                                        setValue(`items.${index}.categoria_id`, String(newValue.categoria_id));
                                                    } else {
                                                        fieldProps.onChange('');
                                                        setValue(`items.${index}.catalogo_item_id`, null);
                                                    }
                                                }}
                                                onInputChange={(event, newInputValue) => {
                                                    if (event?.type === 'change') {
                                                        fieldProps.onChange(newInputValue);
                                                        setValue(`items.${index}.catalogo_item_id`, null);
                                                    }
                                                }}
                                                renderInput={(params) => <TextField {...params} label="Descrição do Item" required error={!!errors.items?.[index]?.descricao} helperText={errors.items?.[index]?.descricao?.message} />}
                                                disabled={!watchedItems[index]?.fase_obra_id}
                                                noOptionsText={!watchedItems[index]?.fase_obra_id ? "Selecione uma fase primeiro" : "Nenhum item encontrado"}
                                            />
                                        )} />
                                    </Box>

                                    <Box sx={{ flex: 1 }}>
                                        <Controller name={`items.${index}.categoria_id`} control={control} render={({ field: fieldProps }) => (
                                            <Autocomplete
                                                fullWidth
                                                options={getHierarchicalCategories(categorias)}
                                                getOptionLabel={(option) => option.nome}
                                                value={categorias.find(c => String(c.id) === String(fieldProps.value)) || null}
                                                onChange={(event, newValue) => fieldProps.onChange(newValue ? String(newValue.id) : '')}
                                                renderOption={(props, option) => {
                                                    const hierOption = option as any;
                                                    return (
                                                        <li {...props} style={{ paddingLeft: `${(hierOption.level * 20) + 10}px` }}>
                                                            {hierOption.level > 0 ? '— '.repeat(hierOption.level) : ''}{option.nome}
                                                        </li>
                                                    );
                                                }}
                                                getOptionDisabled={(option) => (option as any).isParent}
                                                renderInput={(params) => <TextField {...params} label="Categoria" required error={!!errors.items?.[index]?.categoria_id} helperText={errors.items?.[index]?.categoria_id?.message} />}
                                                disabled={!!watchedItems[index]?.catalogo_item_id}
                                            />
                                        )} />
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', gap: 2, width: '100%', alignItems: 'flex-start' }}>
                                    <Controller name={`items.${index}.quantidade`} control={control} render={({ field: fieldProps }) => (
                                        <TextField {...fieldProps} label="Qtd." type="number" required sx={{ flex: 1 }} error={!!errors.items?.[index]?.quantidade} helperText={errors.items?.[index]?.quantidade?.message} />
                                    )} />
                                    <Controller name={`items.${index}.valor_unitario`} control={control} render={({ field: fieldProps }) => {
                                        const catItem = watchedItems[index]?.catalogo_item_id
                                            ? catalogoItens.find(ci => ci.id === Number(watchedItems[index]?.catalogo_item_id))
                                            : null;
                                        const hasPriceDiff = Boolean(
                                            catItem &&
                                            Number(fieldProps.value) > 0 &&
                                            Math.abs(Number(fieldProps.value) - Number(catItem.valor_unitario_padrao || 0)) >= 0.01
                                        );
                                        const diffHelperText = hasPriceDiff && catItem
                                            ? `Valor no catálogo: ${formatCurrency(Number(catItem.valor_unitario_padrao || 0))}`
                                            : undefined;

                                        return (
                                            <TextField
                                                {...fieldProps}
                                                label="Valor Unit."
                                                type="number"
                                                required
                                                sx={{ flex: 1 }}
                                                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                                                error={!!errors.items?.[index]?.valor_unitario}
                                                helperText={errors.items?.[index]?.valor_unitario?.message || diffHelperText}
                                            />
                                        );
                                    }} />
                                    
                                    {watchedTipoDesconto === 'item' && (
                                        <Controller name={`items.${index}.desconto_valor`} control={control} render={({ field: fieldProps }) => (
                                            <TextField {...fieldProps} label="Desconto" type="number" sx={{ flex: 1 }} InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} error={!!errors.items?.[index]?.desconto_valor} helperText={errors.items?.[index]?.desconto_valor?.message} />
                                        )} />
                                    )}
                                    
                                    <TextField label="Total Líquido (Aprox)" value={
                                        formatCurrency(
                                            ((Number(watchedItems[index]?.quantidade) || 0) * (Number(watchedItems[index]?.valor_unitario) || 0)) - 
                                            (watchedTipoDesconto === 'item' ? (Number(watchedItems[index]?.desconto_valor) || 0) : 0)
                                        )
                                    } InputProps={{ readOnly: true }} sx={{ flex: 1, backgroundColor: '#f5f5f5' }} />
                                    
                                    <IconButton onClick={() => remove(index)} color="error" disabled={itemsFields.length <= 1} sx={{ mt: 1 }}><RemoveCircleOutlineIcon /></IconButton>
                                </Box>
                            </Box>
                        ))}
                        <Button startIcon={<AddCircleOutlineIcon />} onClick={() => append({ fase_obra_id: '', categoria_id: '', descricao: '', catalogo_item_id: null, quantidade: 1, valor_unitario: 0, desconto_valor: 0 })} sx={{ mt: 1, alignSelf: 'center' }}>Adicionar Item</Button>
                    </Box>
                    <Divider sx={{ my: 2 }}>Descontos</Divider>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        <Controller name="tipo_desconto" control={control} render={({ field }) => (
                            <FormControl component="fieldset" sx={{ flex: '1 1 100%' }}>
                                <FormLabel component="legend">Aplicar Desconto</FormLabel>
                                <RadioGroup row {...field}>
                                    <FormControlLabel value="total" control={<Radio />} label="No Valor Total" />
                                    <FormControlLabel value="item" control={<Radio />} label="Por Item" />
                                </RadioGroup>
                            </FormControl>
                        )} />

                        {watchedTipoDesconto === 'total' && (
                            <Box sx={{ flex: '1 1 40%', display: 'flex', alignItems: 'flex-start' }}>
                                <Controller name={descontoTipoUI === 'valor' ? 'desconto_valor' : 'desconto_percentual'} control={control} render={({ field }) => (
                                    <TextField 
                                        {...field} 
                                        fullWidth 
                                        label="Desconto" 
                                        type="number" 
                                        error={!!errors[descontoTipoUI === 'valor' ? 'desconto_valor' : 'desconto_percentual']} 
                                        helperText={errors[descontoTipoUI === 'valor' ? 'desconto_valor' : 'desconto_percentual']?.message}
                                        InputProps={{ startAdornment: <InputAdornment position="start">{descontoTipoUI === 'valor' ? 'R$' : '%'}</InputAdornment> }} 
                                    />
                                )} />
                                <ToggleButtonGroup value={descontoTipoUI} exclusive onChange={handleDescontoTipoChange} size="small" sx={{ ml: 1, mt: 1 }}>
                                    <ToggleButton value="valor">R$</ToggleButton>
                                    <ToggleButton value="percentual">%</ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                        <Button variant="outlined" onClick={handleBack}>← Voltar</Button>
                        <Button variant="contained" onClick={handleNext}>Próximo: Anexos →</Button>
                    </Box>
                    </>
                    )}

                    {/* STEP 2: Anexos */}
                    {activeStep === 2 && (
                    <>
                    <Box sx={{ width: '100%', mt: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <input type="file" multiple ref={fileInputRef} onChange={handleFileChange} style={{ display: 'none' }} accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx" />
                            <Button variant="outlined" startIcon={<AttachFileIcon />} onClick={() => fileInputRef.current?.click()}>Anexar Arquivos</Button>
                        </Box>
                        {anexos.length > 0 && (
                            <List dense>
                                {anexos.map((file, index) => (
                                    <ListItem key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                                            <Typography variant="body1">{file.name}</Typography>
                                            <Typography variant="body2" color="textSecondary">{`${(file.size / 1024).toFixed(2)} KB`}</Typography>
                                        </Box>
                                        <IconButton edge="end" aria-label="delete" onClick={() => handleRemoveAnexo(index)} size="small"><DeleteIcon /></IconButton>
                                    </ListItem>
                                ))}
                            </List>
                        )}
                        {initialData?.anexos && initialData.anexos.length > 0 && (
                            <>
                                <Typography variant="subtitle2" sx={{ mt: 2 }}>Anexos Existentes:</Typography>
                                <List dense>
                                    {initialData.anexos.filter((a: any) => !anexosDeletadosIds.includes(a.id)).map((anexo: any) => (
                                        <ListItem key={anexo.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                                                <Typography variant="body1">{anexo.nome_original}</Typography>
                                            </Box>
                                            <IconButton edge="end" aria-label="delete" onClick={() => handleRemoveExistingAnexo(anexo.id)} size="small"><DeleteIcon /></IconButton>
                                        </ListItem>
                                    ))}
                                </List>
                            </>
                        )}
                        {anexos.length === 0 && (!initialData?.anexos || initialData.anexos.filter((a: any) => !anexosDeletadosIds.includes(a.id)).length === 0) && (
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>Nenhum anexo adicionado. Clique no botão acima para anexar arquivos.</Typography>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                        <Button variant="outlined" onClick={handleBack}>← Voltar</Button>
                        <Button variant="contained" onClick={handleNext}>Próximo: Revisão →</Button>
                    </Box>
                    </>
                    )}

                    {/* STEP 3: Observações e Revisão */}
                    {activeStep === 3 && (
                    <>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                        <Controller name="observacoes" control={control} render={({ field }) => (
                            <TextField {...field} label="Observações" multiline rows={3} fullWidth sx={{ flex: '1 1 100%' }} />
                        )} />
                        
                        <Box sx={{ width: '100%', mt: 2, display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                            <Button variant="outlined" onClick={handleBack}>← Voltar</Button>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                {isEditMode && (
                                    <Button onClick={onCancel} variant="outlined">Cancelar</Button>
                                )}
                                <Button type="submit" variant="contained" color="primary" disabled={loadingData || isLoadingSubmit}>Revisar Lançamento</Button>
                            </Box>
                        </Box>
                    </Box>
                    </>
                    )}

                    {/* Sticky Footer - Totais em Tempo Real */}
                    <Paper
                        elevation={6}
                        sx={{
                            position: 'sticky',
                            bottom: 0,
                            zIndex: 10,
                            mt: 3,
                            p: 2,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #f5f7fa 0%, #e8edf5 100%)',
                            borderTop: '2px solid',
                            borderColor: 'primary.main',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 2,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" color="text.secondary">Valor Bruto:</Typography>
                            <Typography variant="subtitle1" fontWeight={600}>{formatCurrency(valorTotalBruto)}</Typography>
                        </Box>
                        {(valorTotalBruto - valorTotalLiquido) > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" color="error.main">Descontos:</Typography>
                                <Typography variant="subtitle1" fontWeight={600} color="error.main">- {formatCurrency(valorTotalBruto - valorTotalLiquido)}</Typography>
                            </Box>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" color="primary.main">Total Líquido:</Typography>
                            <Typography variant="h6" fontWeight={700} color="primary.main">{formatCurrency(valorTotalLiquido)}</Typography>
                        </Box>
                    </Paper>
                </>
            ) : (
                renderReviewSummary()
            )}

            {/* Modal de Confirmação para Atualizar Valor Padrão no Catálogo */}
            <Dialog
                open={priceUpdateModalOpen}
                onClose={() => !isUpdatingPrices && setPriceUpdateModalOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Atualizar Valor Padrão no Catálogo?</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Os itens abaixo foram lançados com valor unitário diferente do cadastrado no catálogo.
                        Selecione quais itens deseja atualizar com o novo valor padrão:
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'action.hover' }}>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            indeterminate={
                                                selectedCatalogIds.length > 0 &&
                                                selectedCatalogIds.length < divergentItems.length
                                            }
                                            checked={
                                                divergentItems.length > 0 &&
                                                selectedCatalogIds.length === divergentItems.length
                                            }
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedCatalogIds(divergentItems.map(d => d.catalogo_item_id));
                                                } else {
                                                    setSelectedCatalogIds([]);
                                                }
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell><strong>Item do Catálogo</strong></TableCell>
                                    <TableCell align="right"><strong>Valor Cadastrado</strong></TableCell>
                                    <TableCell align="right"><strong>Novo Valor</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {divergentItems.map((item) => {
                                    const isChecked = selectedCatalogIds.includes(item.catalogo_item_id);
                                    const isZeroDefault = item.valor_catalogo === 0;
                                    return (
                                        <TableRow
                                            key={item.catalogo_item_id}
                                            hover
                                            onClick={() => {
                                                setSelectedCatalogIds(prev =>
                                                    prev.includes(item.catalogo_item_id)
                                                        ? prev.filter(id => id !== item.catalogo_item_id)
                                                        : [...prev, item.catalogo_item_id]
                                                );
                                            }}
                                            sx={{ cursor: 'pointer' }}
                                        >
                                            <TableCell padding="checkbox">
                                                <Checkbox checked={isChecked} />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={500}>{item.descricao}</Typography>
                                                {isZeroDefault && (
                                                    <Chip
                                                        label="Valor variável (R$ 0,00)"
                                                        size="small"
                                                        variant="outlined"
                                                        color="warning"
                                                        sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell align="right" sx={{ color: 'text.secondary' }}>
                                                {formatCurrency(item.valor_catalogo)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                                {formatCurrency(item.novo_valor)}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Button
                        onClick={() => setPriceUpdateModalOpen(false)}
                        disabled={isUpdatingPrices || isLoadingSubmit}
                        color="inherit"
                    >
                        Voltar
                    </Button>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            onClick={() => executeFinalSubmit(false)}
                            variant="outlined"
                            disabled={isUpdatingPrices || isLoadingSubmit}
                        >
                            Não atualizar catálogo (Apenas Salvar)
                        </Button>
                        <Button
                            onClick={() => executeFinalSubmit(true)}
                            variant="contained"
                            color="primary"
                            disabled={isUpdatingPrices || isLoadingSubmit || selectedCatalogIds.length === 0}
                        >
                            {(isUpdatingPrices || isLoadingSubmit)
                                ? <CircularProgress size={22} />
                                : `Atualizar (${selectedCatalogIds.length}) e Salvar`}
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
